import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  checkWrappedAccess,
  getWrappedData,
  markWrappedComplete,
  markWrappedShared,
  WrappedData,
  WrappedAccessResponse,
} from '@/api/wrapped';

export function useWrappedAccess() {
  return useQuery<WrappedAccessResponse, Error>({
    queryKey: ['wrapped', 'access'],
    queryFn: checkWrappedAccess,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

export function useWrappedData() {
  return useQuery<WrappedData, Error>({
    queryKey: ['wrapped', 'data', '2025'],
    queryFn: getWrappedData,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 1,
  });
}

export function useWrappedComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markWrappedComplete,
    onSuccess: (data) => {
      // Update the wrapped data in cache to reflect points awarded
      queryClient.invalidateQueries({ queryKey: ['wrapped', 'data'] });
      // Also invalidate points balance since user earned points
      queryClient.invalidateQueries({ queryKey: ['points'] });
      return data;
    },
  });
}

export function useWrappedShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markWrappedShared,
    onSuccess: (data) => {
      // Update the wrapped data in cache to reflect points awarded
      queryClient.invalidateQueries({ queryKey: ['wrapped', 'data'] });
      // Also invalidate points balance since user earned points
      queryClient.invalidateQueries({ queryKey: ['points'] });
      return data;
    },
  });
}

// Combined hook for convenience
export function useWrapped() {
  const access = useWrappedAccess();
  const data = useWrappedData();
  const complete = useWrappedComplete();
  const share = useWrappedShare();

  return {
    // Access check
    canAccess: access.data?.allowed ?? false,
    accessReason: access.data?.reason,
    isAccessLoading: access.isLoading,
    accessError: access.error,

    // Wrapped data
    data: data.data,
    isLoading: data.isLoading,
    error: data.error,
    refetch: data.refetch,

    // Actions
    markComplete: complete.mutate,
    isCompletingView: complete.isPending,
    completeError: complete.error,

    markShared: share.mutate,
    isMarkingShared: share.isPending,
    shareError: share.error,
  };
}
